import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { combinePermission } from 'utils/permission';
import {
  CompanyPDP,
  ICompany,
  ICompanyMember,
  ICompanyPermissionScheme,
  ICompanyProviders,
  ICompanyRole,
  ICompanyRoleServer,
  ICompanyServer,
  ICompanyState,
  ICompanyTemplate,
  IInvitedMember,
  IProject,
  IProjectMemberView,
  IProjectStore,
  IProjectTemplate,
  IStreamingConfig,
  IUserRoleView,
} from './companiesTypes';
import * as manApi from 'api/ManApi';
import { RootState, TThunkApi } from 'store/store';
import { requireSeveralUser } from 'store/users/usersSlice';
import {
  companySetTemplateData,
  task_setCompaniesData,
  tasks_getCompany,
  tasks_setCompanyTemplateData,
  tasks_setSingleProject,
} from 'store/tasks/tasksSlice';
import { updateNestedObject } from '../objectHelpers';
import { getCookie, setCookie } from '../../utils/happyFunctions';
import UserMixpanel from '../../utils/mixpanel/MixpanelClass';
import i18n from '../../i18n';

const initialState: ICompanyState = {
  data: {},
  status: '',
};
// Helpers
// function getThisUserRoleIds(
//   state: RootState,
//   companyId: number,
//   thisUserId: number
// ) {
//   return (Object.keys(
//     state.companies.data[companyId].members[thisUserId].roles
//   ).map((it) => Number(it)) || []) as Array<number>;
// }

const projectManipulate = (
  project: IProject | IProjectStore,
  thisUserId: number,
  company: ICompany | ICompanyServer
) => {
  const newProject: IProjectStore = { ...project } as any;
  const companyRoles = !company.roles
    ? []
    : Array.isArray(company.roles)
      ? company?.roles
      : Object.values(company.roles as { [key: number]: ICompanyRole });
  const thisUserRoleIds = companyRoles
    .filter((r) => r.users.find((ur) => ur.userId === thisUserId))
    .map((r) => r.id);

  if (!project.stub) {
    const tempMembers = Array.isArray(project.projectMembers)
      ? project.projectMembers
      : Object.values(project.projectMembers || {});
    const splitMembers = tempMembers.reduce(
      (
        accu: {
          userMembers: { [userId: number]: IProjectMemberView };
          roleMembers: { [userId: number]: IProjectMemberView };
        },
        curr
      ) => {
        if (curr.userId) {
          accu.userMembers[curr.userId] = curr;
        } else if (curr.roleId) {
          accu.roleMembers[curr.roleId] = curr;
        }
        return accu;
      },
      { userMembers: {}, roleMembers: {} }
    );
    newProject.projectMembers = splitMembers.userMembers;
    newProject.projectRoleMembers = splitMembers.roleMembers;
    newProject.myMembership = combinePermission(
      splitMembers.userMembers[thisUserId],
      Object.values(splitMembers.roleMembers),
      thisUserId,
      thisUserRoleIds
    );
    newProject.templates = project.templates!.reduce(
      (accu: IProjectTemplate[], curr) => {
        accu.push(curr);
        return accu;
      },
      []
    );
  } else {
    newProject.projectMembers = {};
    newProject.projectRoleMembers = {};
    newProject.templates = [];
  }
  return newProject;
};

function update(state: ICompanyState, key: string | number, value: any) {
  if (state.data[key]) {
    state.data = {
      ...state.data,
      [key]: {
        ...state.data[key],
        ...value,
      },
    };
  } else {
    state.data = {
      ...state.data,
      [key]: { ...value },
    };
  }
}

function getDisplayName(role: ICompanyRole) {
  let ret = role.name;
  if (role.roleType === 'District') {
    ret = i18n.t('personel', { ns: 'companyManagement' }) + ' ' + role.name;
  }
  return ret;
}

const computeUserRoles = (
  companyRoles: ICompany['roles'],
  memberId: number
): ICompanyMember['roles'] => {
  const test = Object.values(companyRoles || {}).reduce((roles, role) => {
    if (role.users.length > 0) {
      const hasThisRole = role.users.find((u) => u.userId === memberId);

      if (hasThisRole) {
        roles[role.id] = {
          id: role.id,
          name: getDisplayName(role),
          sourceRoleId: hasThisRole.roleId,
        };
      }
    }
    return roles as ICompanyMember['roles'];
  }, {});
  return test;
};

const companyManipulate = (
  company: ICompanyServer | ICompany,
  thisUserId: number,
  current?: ICompany
) => {
  const newCompany: any = { ...company };
  /*
    Todo : If Back-end send company array instead of null for members, projects and templates
    you should remove bellow conditional statement
  */
  if (
    company.configFlag['forceBeta'] === 'true' &&
    getCookie('useBeta') !== 'true'
  ) {
    setCookie('useBeta', 'true');
    setTimeout(() => {
      window.location.reload();
    }, 50);
  }
  const members = Array.isArray(company.members)
    ? company.members
    : Object.values(company.members || {});
  const projects = Array.isArray(company.projects)
    ? company.projects
    : Object.values(company.projects || {});
  const roles = Array.isArray(company.roles)
    ? company.roles
    : Object.values(company.roles || {});
  const templates = Array.isArray(company.templates)
    ? company.templates
    : Object.values(company.templates || {});
  const permissionSchemes = Array.isArray(company.permissionSchemes)
    ? company.permissionSchemes
    : Object.values(company.permissionSchemes || {});

  if (members.length > 0) {
    newCompany.members = members.reduce((accu, member) => {
      member.roles = {};
      if (roles.length > 0) {
        member = {
          ...member,
          roles: computeUserRoles(roles, member.userId),
        };
      } else {
        member = {
          ...member,
          roles: {},
        };
      }

      accu[member.userId] = member;

      return accu;
    }, {});

    newCompany.admins = members
      .filter((m) => m.admin)
      .reduce(
        (accu: number[], member) => {
          accu.push(member.userId);
          return accu;
        },
        [company.owner.userId]
      );
  } else {
    newCompany.members = current?.members || {};
    newCompany.admins = current?.admins || [company.owner.userId];
  }

  if (projects.length !== 0) {
    newCompany.projects = projects.reduce(
      (accu: { [key: number]: IProjectStore }, project) => {
        const newProject = projectManipulate(project, thisUserId, company);
        accu[project.id] = { ...newProject, isLoading: false };
        return accu;
      },
      {}
    );
  } else {
    newCompany.projects = current?.projects || {};
  }

  if (templates !== null) {
    newCompany.templates = templates.reduce(
      (accu, curr) => {
        const template = { ...curr };
        delete template.task;
        accu[template.taskId] = template;

        return accu;
      },
      {} as { [key: number]: Omit<ICompanyTemplate, 'task'> }
    );
  } else {
    newCompany.templates = current?.templates || {};
  }

  if (roles.length > 0) {
    newCompany.roles = roles.reduce(
      (roles, role) => {
        role.displayName = getDisplayName(role);
        roles[role.id] = role;
        return roles;
      },
      {} as { [key: number]: ICompanyRole }
    );
  } else {
    newCompany.roles = current?.roles || {};
  }

  if (permissionSchemes.length > 0) {
    newCompany.permissionSchemes = permissionSchemes.reduce(
      (permissionSchemes, permissionScheme) => {
        permissionSchemes[permissionScheme.id!] = permissionScheme;
        return permissionSchemes;
      },
      {} as { [key: number]: ICompanyPermissionScheme }
    );
  } else {
    newCompany.permissionSchemes = current?.permissionSchemes || {};
  }

  newCompany.relatedProviders = current?.relatedProviders || {};
  return newCompany;
};

// Reducers
function loadingCompaniesHandler(state: ICompanyState) {
  state.status = 'loading';
}

function notLoadedCompaniesHandler(state: ICompanyState) {
  state.status = 'notLoaded';
}

function setCompaniesDataHandler_actionHandler(
  state: ICompanyState,
  action: PayloadAction<{
    companies: Array<ICompanyServer | ICompany>;
    thisUserId: number;
    noStub: boolean;
  }>
) {
  state.status = 'loaded';
  const mixPanel = UserMixpanel.getInstance(+action.payload.thisUserId);
  let didFoundHospital = false;
  action.payload.companies.forEach((company, index, companies) => {
    const newCompany = companyManipulate(company, action.payload.thisUserId);
    update(state, company.id, newCompany);
    if (
      !didFoundHospital &&
      (company.name.startsWith('بیمارستان') || company.name.startsWith('مرکز'))
    ) {
      mixPanel.setRoleNames(newCompany);
      mixPanel.setCompanyName(newCompany.name);
      didFoundHospital = true;
    }
    if (!didFoundHospital && index === action.payload.companies.length - 1) {
      mixPanel.setRoleNames(companies[0]);
    }

    if (action.payload.noStub) {
      state = updateNestedObject({
        obj: state,
        path: `data[${company.id}]`,
        value: {
          ...(state?.data?.[company.id] || {}),
          status: 'loaded',
        },
      });
    }
  });
}

export const setCompaniesDataFn = async (
  {
    companies,
    thisUserId,
    noStub,
  }: {
    companies: ICompany[];
    thisUserId: number;
    noStub: boolean;
  },
  ThunkApi: TThunkApi
) => {
  ThunkApi.dispatch(
    task_setCompaniesData({
      companies,
      thisUserId,
      noStub,
    })
  );
  ThunkApi.dispatch(
    setCompaniesDataHandler_action({ companies, thisUserId, noStub })
  );
  return Promise.resolve({ companies, thisUserId, noStub });
};
const setCompaniesData = createAsyncThunk(
  'companies/setCompaniesDataHandler',
  setCompaniesDataFn
);

function setSingleCompanyHandler(
  state: ICompanyState,
  action: PayloadAction<{ company: ICompanyServer; thisUserId: number }>
) {
  update(
    state,
    action.payload.company.id,
    companyManipulate(
      action.payload.company,
      action.payload.thisUserId,
      state.data[action.payload.company.id] ?? undefined
    )
  );
}

function setSingleCompanyPDPDataHandler(
  state: ICompanyState,
  action: PayloadAction<{ pdp: CompanyPDP }>
) {
  const pdpPanels = state.data[action.payload.pdp.companyId].pdpPanels.filter(
    (it) => it.id !== action.payload.pdp.id
  );
  pdpPanels.push(action.payload.pdp);
  update(state, action.payload.pdp.companyId, {
    pdpPanels: pdpPanels,
  });
}

function loadingCompanyHandler(
  state: ICompanyState,
  action: PayloadAction<{ companyId: number }>
) {
  update(state, action.payload.companyId, {
    status: 'loading',
  });
}

function getCompanyHandler_action(
  state: ICompanyState,
  action: PayloadAction<{ company: ICompanyServer; thisUserId: number }>
) {
  const company = companyManipulate(
    action.payload.company,
    action.payload.thisUserId,
    state.data[action.payload.company.id] ?? undefined
  );
  company.status = 'loaded';
  const mx = UserMixpanel.getInstance();
  mx.setRoleNames(company);
  mx.setCompanyName(company.name);

  update(state, action.payload.company.id, company);
  // update(state, action.payload.company.id, { status: 'loaded' });
}

export const getCompanyFn = async (
  {
    company,
    thisUserId,
  }: {
    company: ICompanyServer;
    thisUserId: number;
  },
  ThunkApi: TThunkApi
) => {
  ThunkApi.dispatch(tasks_getCompany({ company, thisUserId }));
  ThunkApi.dispatch(getCompanyReducer({ company, thisUserId }));
  return Promise.resolve({ company, thisUserId });
};
const getCompany = createAsyncThunk(
  'companies/getCompanyHandler',
  getCompanyFn
);

function removeCompanyHandler(
  state: ICompanyState,
  action: PayloadAction<{ companyId: number }>
) {
  delete state.data[action.payload.companyId];
}

function setCompanyMemberDataHandler(
  state: ICompanyState,
  action: PayloadAction<{ companyMember: ICompanyMember }>
) {
  let tempCompanyMember = action.payload.companyMember;
  if (tempCompanyMember.userRoles) {
    const t = tempCompanyMember.userRoles.reduce(
      (current, itm) => {
        current[itm.roleId] = {
          id: itm.roleId,
          name: '',
          sourceRoleId: itm.sourceRoleId,
        };
        return current;
      },
      {} as ICompanyMember['roles']
    );

    tempCompanyMember = {
      ...tempCompanyMember,
      roles: t,
    };
  }

  const oldRoleIds = new Set(
    Object.keys(
      state.data[tempCompanyMember.companyId].members[tempCompanyMember.userId]
        .roles || {}
    )
  );

  action.payload.companyMember.userRoles?.forEach((r) => {
    oldRoleIds.delete(String(r.roleId));
  });

  state.data[action.payload.companyMember.companyId].members[
    action.payload.companyMember.userId
  ] = tempCompanyMember;

  tempCompanyMember.userRoles?.forEach((userRole) => {
    const oldUsers = Object.values(
      state.data[tempCompanyMember.companyId].roles[userRole.roleId].users
    );

    const tempUsers = Object.values(oldUsers).filter(
      (it) => it.userId !== userRole.userId
    );

    tempUsers.push(userRole as IUserRoleView);
    state.data[tempCompanyMember.companyId].roles[userRole.roleId].users =
      tempUsers;
  });

  oldRoleIds.forEach((roleIdString) => {
    const roleId = Number(roleIdString);
    const temp = state.data[tempCompanyMember.companyId].roles[roleId].users;
    const filteredTemp =
      temp && Array.isArray(temp)
        ? temp?.filter((it) => it.roleId !== roleId)
        : undefined;
    if (filteredTemp) {
      state.data[tempCompanyMember.companyId].roles[roleId].users =
        filteredTemp;
    }
  });
}

function removeCompanyMemberHandler(
  state: ICompanyState,
  action: PayloadAction<{ companyMember: ICompanyMember }>
) {
  const { userId, companyId } = action.payload.companyMember;
  Object.keys(state.data[companyId].members[userId].roles).forEach((roleId) => {
    state.data[companyId].roles[Number(roleId)].users = state.data[
      companyId
    ].roles[Number(roleId)].users.filter((usr: any) => usr.userId !== userId);
  });

  state = updateNestedObject<ICompanyState>({
    obj: state,
    path: `data[${companyId}].members[${userId}]`,
    value: {
      inactive: true,
      admin: false,
      roles: {},
    },
  });
}

function companiesBatchAddMembersHandler(
  state: ICompanyState,
  action: PayloadAction<{ company: ICompanyServer; thisUserId: number }>
) {
  const company = companyManipulate(
    action.payload.company,
    action.payload.thisUserId
  );
  update(state, action.payload.company.id, { members: company.members });
  update(state, action.payload.company.id, { roles: company.roles });
}

const setTemplateData = createAsyncThunk(
  'companies/setTemplateData',
  async ({ template }: { template: ICompanyTemplate }, ThunkApi: TThunkApi) => {
    ThunkApi.dispatch(setCompanyTemplateDataHandler_action({ template }));
    ThunkApi.dispatch(companySetTemplateData({ template }));
  }
);

function setCompanyTemplateDataHandler_actionHandler(
  state: ICompanyState,
  action: PayloadAction<{ template: ICompanyTemplate }>
) {
  state.data[action.payload.template.companyId].templates[
    action.payload.template.taskId
  ] = action.payload.template;
}

export const setCompanyTemplateDataFn = async (
  {
    template,
  }: {
    template: ICompanyTemplate;
  },
  ThunkApi: TThunkApi
) => {
  ThunkApi.dispatch(tasks_setCompanyTemplateData({ template }));
  ThunkApi.dispatch(setCompanyTemplateDataHandler_action({ template }));
  return Promise.resolve({ template });
};
const setCompanyTemplateData = createAsyncThunk(
  'companies/setCompanyTemplateDataHandler',
  setCompanyTemplateDataFn
);

function companiesSetRoleDataHandler(
  state: ICompanyState,
  action: PayloadAction<{ role: ICompanyRole }>
) {
  if (!action.payload.role.companyId) return;

  state = updateNestedObject({
    obj: state,
    path: `state.data[${action.payload.role.companyId}].roles[${action.payload.role.id}]`,
    value: action.payload.role,
  });
}

function companiesSetRolesDataHandler(
  state: ICompanyState,
  action: PayloadAction<{
    updateRole: manApi.UpdateRoleObject;
    companyId: number;
  }>
) {
  action.payload.updateRole.updateRoles.forEach((it) => {
    it.displayName = getDisplayName(it);

    state = updateNestedObject({
      obj: state,
      path: `data[${action.payload.companyId}].roles[${it.id}]`,
      value: it,
    });
  });

  action.payload.updateRole.deleted.forEach((it) => {
    delete state.data[action.payload.companyId]?.roles[it];
  });

  Object.values(state.data[action.payload.companyId].members).forEach(
    (member) => {
      action.payload.updateRole.deleted.forEach((it) => {
        delete state.data[action.payload.companyId]?.members[member.userId]
          ?.roles[it];
      });
    }
  );
}

function companiesDeleteRoleDataHandler(
  state: ICompanyState,
  action: PayloadAction<{ roleId: number; companyId: number }>
) {
  delete state.data[action.payload.companyId]?.roles[action.payload.roleId];

  Object.values(state.data[action.payload.companyId].members).forEach(
    (member) => {
      delete state.data[action.payload.companyId]?.members[member.userId]
        ?.roles[action.payload.roleId];
    }
  );
}

function companiesSetPermissionSchemeDataHandler(
  state: ICompanyState,
  action: PayloadAction<{ permissionScheme: ICompanyPermissionScheme }>
) {
  if (!action.payload.permissionScheme.id) return;

  state = updateNestedObject({
    obj: state,
    path: `data[${action.payload.permissionScheme.companyId}].permissionSchemes[${action.payload.permissionScheme.id}]`,
    value: action.payload.permissionScheme,
  });
}

function companiesDeletePermissionSchemeDataHandler(
  state: ICompanyState,
  action: PayloadAction<{ permissionSchemeId: number; companyId: number }>
) {
  delete state.data[action.payload.companyId]?.permissionSchemes[
    action.payload.permissionSchemeId
  ];
}

function companiesAddUserRolesHandler(
  state: ICompanyState,
  action: PayloadAction<{ userRoles: IUserRoleView[]; companyId: number }>
) {
  action.payload.userRoles.forEach((userRole) => {
    const { roleId, userId } = userRole;

    if (!state.data[action.payload.companyId]) {
      // @ts-expect-error it's updating step by step
      state.data = {
        ...state.data,
        [action.payload.companyId]: {
          roles: {},
          members: {},
        },
      };
    }

    if (!state.data[action.payload.companyId].roles[roleId]) {
      // @ts-expect-error it's updating step by step
      state.data[action.payload.companyId].roles = {
        ...state.data[action.payload.companyId].roles,
        [roleId]: {
          users: [],
        },
      };
    }

    state.data[action.payload.companyId].roles[roleId].users.push(userRole);
    const roleName = state.data[action.payload.companyId].roles[roleId].name;
    const memebrRole = {
      id: roleId,
      name: roleName,
      sourceRoleId: roleId,
    };

    if (!state.data[action.payload.companyId].members[userId].roles[roleId]) {
      // @ts-expect-error it's updating step by step
      state.data[action.payload.companyId].members = {
        ...state.data[action.payload.companyId].members,
        [userId]: {
          roles: {},
        },
      };
    }

    state = updateNestedObject({
      obj: state,
      path: `data[${action.payload.companyId}].members[${userId}].roles`,
      value: {
        [roleId]: memebrRole,
      },
    });
  });
}

function companiesRemoveUserRolesHandler(
  state: ICompanyState,
  action: PayloadAction<{ userRoles: IUserRoleView[]; companyId: number }>
) {
  action.payload.userRoles.forEach(({ roleId, userId }) => {
    if (!state.data[action.payload.companyId]) {
      // @ts-expect-error it's updating step by step
      state.data = {
        ...state.data,
        [action.payload.companyId]: {
          roles: {},
        },
      };
    }

    if (!state.data[action.payload.companyId].roles[roleId].users) {
      // @ts-expect-error it's updating step by step
      state.data[action.payload.companyId].roles = {
        ...state.data[action.payload.companyId].roles,
        [roleId]: {
          users: [],
        },
      };
    }

    state.data[action.payload.companyId].roles[roleId].users = state.data[
      action.payload.companyId
    ].roles[roleId].users.filter((it) => it.userId !== userId);

    delete state.data[action.payload.companyId].members[userId].roles[roleId];
  });
}

function addCompanyProvidersHandler(
  state: ICompanyState,
  action: PayloadAction<{
    companyId: number;
    relatedProviders: ICompanyProviders;
  }>
) {
  Object.values(action.payload.relatedProviders || {}).forEach((provider) => {
    state = updateNestedObject({
      obj: state,
      path: `data[${action.payload.companyId}].relatedProviders[${provider.id}]`,
      value: provider,
    });
  });
}

function createProjectHandler(
  state: ICompanyState,
  action: PayloadAction<{
    project: IProject;
    thisUserId: number;
  }>
) {
  const company = state.data[action.payload.project.companyId];
  state = updateNestedObject({
    obj: state,
    path: `data[${action.payload.project.companyId}].projects[${action.payload.project.id}]`,
    value: projectManipulate(
      action.payload.project,
      action.payload.thisUserId,
      company
    ),
  });
}

// Projects
function setProjectMemberDataHandler(
  state: ICompanyState,
  action: PayloadAction<{
    projectMember: IProjectMemberView;
    companyId: number;
  }>
) {
  if (action.payload.projectMember.userId)
    state.data[action.payload.companyId].projects[
      action.payload.projectMember.projectId
    ].projectMembers[action.payload.projectMember.userId] =
      action.payload.projectMember;
  else
    state.data[action.payload.companyId].projects[
      action.payload.projectMember.projectId
    ].projectRoleMembers[action.payload.projectMember.roleId!.toString()] =
      action.payload.projectMember;
  // state = updateNestedObject({
  //   obj: state,
  //   path: `data[${action.payload.companyId}].projects[${action.payload.projectMember.projectId}][
  //     ${
  //       action.payload.projectMember.userId
  //         ? 'projectMembers'
  //         : 'projectRoleMembers'
  //     }
  //   ][
  //   ${(action.payload.projectMember.userId ||
  //     action.payload.projectMember.roleId)!.toString()}
  // ]`,
  //   value: action.payload.projectMember,
  // });
}

function removeProjectMemberHandler(
  state: ICompanyState,
  action: PayloadAction<{
    projectMember: IProjectMemberView;
    companyId: number;
  }>
) {
  delete state.data[action.payload.companyId].projects[
    action.payload.projectMember.projectId
  ]?.[
    action.payload.projectMember.userId
      ? 'projectMembers'
      : 'projectRoleMembers'
  ]?.[
    (action.payload.projectMember.userId ||
      action.payload.projectMember.roleId)!.toString()
  ];
}

function setCompanyDownStream(
  state: ICompanyState,
  action: PayloadAction<{
    companyId: number;
    streamingConfig: IStreamingConfig;
  }>
) {
  if (state.data[action.payload.companyId]) {
    state.data[action.payload.companyId].streamingConfig =
      action.payload.streamingConfig;
  }
}

function loadingSingleProjectHandler(
  state: ICompanyState,
  action: PayloadAction<{
    projectId: number;
    companyId: number;
  }>
) {
  state.data[action.payload.companyId].projects[
    action.payload.projectId
  ].isLoading = true;
}

function setSingleProjectHandler(
  state: ICompanyState,
  action: PayloadAction<{
    project: IProject;
    thisUserId: number;
  }>
) {
  const company = state.data[action.payload.project.companyId];
  state.data[action.payload.project.companyId].projects[
    action.payload.project.id
  ] = projectManipulate(
    action.payload.project,
    action.payload.thisUserId,
    company
  );
  state.data[action.payload.project.companyId].projects[
    action.payload.project.id
  ].isLoading = false;
}

function setProjectTemplateDataHandler(
  state: ICompanyState,
  action: PayloadAction<{
    projectTemplate: IProjectTemplate;
    companyId: number;
  }>
) {
  const temp =
    state.data[action.payload.companyId].projects[
      action.payload.projectTemplate.projectId
    ].templates;
  const size = temp.length;
  temp.push(action.payload.projectTemplate);

  state.data[action.payload.companyId].projects[
    action.payload.projectTemplate.projectId
  ].templates = temp.filter(
    (tempId, idx) =>
      tempId.taskId !== action.payload.projectTemplate.taskId || idx === size
  );
}

function removeTemplateHandler(
  state: ICompanyState,
  action: PayloadAction<{
    projectTemplate: IProjectTemplate;
    companyId: number;
  }>
) {
  const temp =
    state.data[action.payload.companyId].projects[
      action.payload.projectTemplate.projectId
    ].templates;
  state.data[action.payload.companyId].projects[
    action.payload.projectTemplate.projectId
  ].templates = temp.filter((templateId) => {
    return templateId.taskId !== action.payload.projectTemplate.taskId;
  });
}

function setCompaniesDataHandler(
  state: ICompanyState,
  action: PayloadAction<{
    thisUserId: number;
    noStub: boolean;
  }>
) {
  Object.values(state.data || {}).forEach((company) => {
    Object.values(company.projects).forEach((project) => {
      state = updateNestedObject({
        obj: state,
        path: `data[${company.id}].projects[${project.id}]`,
        value: projectManipulate(project, action.payload.thisUserId, company),
      });
    });
  });
}

function updateCompanyProjectHandler(
  state: ICompanyState,
  action: PayloadAction<{ company: ICompany; thisUserId: number }>
) {
  Object.values(state.data || {}).forEach((company) =>
    Object.values(company.projects || {}).forEach((project) => {
      const stub = project.stub;
      if (stub) {
        const newProject = projectManipulate(
          project,
          action.payload.thisUserId,
          company
        );

        state.data[company.id].projects[project.id] = {
          ...state.data[project.id],
          ...newProject,
        };
      }
    })
  );
}

const setSingleProject = createAsyncThunk(
  'project/setSingleProject',
  async (
    {
      project,
      thisUserId,
    }: {
      project: IProject;
      thisUserId: number;
    },
    ThunkApi: TThunkApi
  ) => {
    ThunkApi.dispatch(setSingleProjectAction({ project, thisUserId }));

    ThunkApi.dispatch(tasks_setSingleProject({ project }));
  }
);

export const createCompanyProjectFn = async (
  { project }: { project: IProject },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .createProject(project, store)
    .then(({ data: createdProject }) => {
      const thisUserId = Number(store.thisUser.userId);
      ThunkApi.dispatch(
        createProjectAction({
          project: createdProject,
          thisUserId,
        })
      );
      return Promise.resolve({ createdProject });
    })
    .catch((err) => {
      return Promise.reject({ err });
    });
};
const createCompanyProject = createAsyncThunk(
  'projects/createProject',
  createCompanyProjectFn
);

export const createProjectsFn = async (
  {
    project,
    names,
    templates,
  }: {
    project: IProject;
    names: Array<string>;
    templates: Array<IProjectTemplate>;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.createBatchProject(project, names, templates, store).then(
    ({ data: createdProjects }) => {
      const thisUserId = Number(store.thisUser.userId);
      createdProjects.forEach((createdProject) => {
        ThunkApi.dispatch(
          createProjectAction({
            project: createdProject,
            thisUserId,
          })
        );
      });
      return Promise.resolve({ createdProjects });
    },
    (err) => {
      return Promise.reject({ err });
    }
  );
};
const createProjects = createAsyncThunk(
  'projects/createProjects',
  createProjectsFn
);

export const getProjectFn = async (
  { projectId, companyId }: { projectId: number; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const project = store.companies.data[companyId].projects[projectId];
  if (!project || project.stub) {
    return manApi.getProject(projectId, store).then(
      ({ data: receivedProject }) => {
        const thisUserId = Number(store.thisUser.userId);

        ThunkApi.dispatch(
          setSingleProject({
            project: receivedProject,
            thisUserId,
          })
        );
        return Promise.resolve();
      },
      (err) => Promise.reject({ err })
    );
  }
  return Promise.resolve();
};
const getProject = createAsyncThunk('projects/getProject', getProjectFn);

export const addProjectMemberFn = async (
  {
    projectMember,
    companyId,
  }: { projectMember: IProjectMemberView; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.addProjectMember(projectMember, store).then(
    ({ data: projectMemberData }) => {
      ThunkApi.dispatch(
        setProjectMemberData({ projectMember: projectMemberData, companyId })
      );
      return Promise.resolve({ projectMemberData });
    },
    (err) => {
      return Promise.reject({ err });
    }
  );
};
const addProjectMember = createAsyncThunk(
  'projects/addProjectMember',
  addProjectMemberFn
);

export const removeProjectMemberFn = async (
  {
    projectMember,
    companyId,
  }: { projectMember: IProjectMemberView; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .removeProjectMember(projectMember, store)
    .then(() => {
      ThunkApi.dispatch(
        removeProjectMemberAction({ projectMember: projectMember, companyId })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return Promise.reject({ err });
    });
};
const removeProjectMember = createAsyncThunk(
  'projects/removeProjectMember',
  removeProjectMemberFn
);

export const updateProjectMemberFn = async (
  {
    projectMember,
    companyId,
  }: { projectMember: IProjectMemberView; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateProjectMember(projectMember, store)
    .then(({ data: projectMemberData }) => {
      ThunkApi.dispatch(
        setProjectMemberData({ projectMember: projectMemberData, companyId })
      );
      return Promise.resolve({ projectMemberData });
    })
    .catch((err) => Promise.reject({ err }));
};
const updateProjectMember = createAsyncThunk(
  'projects/updateProjectMember',
  updateProjectMemberFn
);

export const updateProjectFn = async (
  { project }: { project: IProject },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateProject(project, store)
    .then(({ data: updatedProject }) => {
      const thisUserId = Number(store.thisUser.userId);
      ThunkApi.dispatch(
        setSingleProject({
          project: updatedProject,
          thisUserId,
        })
      );
      return Promise.resolve({ updatedProject });
    })
    .catch((err) => Promise.reject({ err }));
};
const updateProject = createAsyncThunk(
  'projects/updateProject',
  updateProjectFn
);

export const addProjectTemplateFn = async (
  {
    projectTemplate,
    companyId,
  }: { projectTemplate: IProjectTemplate; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  ThunkApi.dispatch(
    setProjectTemplateData({ projectTemplate: projectTemplate, companyId })
  );
  return manApi
    .addTemplateToProject(projectTemplate, store)
    .then(({ data: projectTemplateData }) => {
      ThunkApi.dispatch(
        setProjectTemplateData({
          projectTemplate: projectTemplateData,
          companyId,
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      ThunkApi.dispatch(
        removeTemplate({ projectTemplate: projectTemplate, companyId })
      );
      return Promise.reject({ err });
    });
};
const addProjectTemplate = createAsyncThunk(
  'projects/addProjectTemplate',
  addProjectTemplateFn
);

export const removeProjectTemplateFn = async (
  {
    projectTemplate,
    companyId,
  }: { projectTemplate: IProjectTemplate; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  ThunkApi.dispatch(
    removeTemplate({ projectTemplate: projectTemplate, companyId })
  );
  return manApi
    .removeTemplateFromProject(projectTemplate, store)
    .then(() => {
      return Promise.resolve();
    })
    .catch((err) => {
      ThunkApi.dispatch(
        setProjectTemplateData({ projectTemplate: projectTemplate, companyId })
      );
      return Promise.reject({ err });
    });
};
const removeProjectTemplate = createAsyncThunk(
  'projects/removeProjectTemplate',
  removeProjectTemplateFn
);

// Thunks
export const getMyCompaniesFn = async (
  { saveResults = true }: { saveResults?: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const companies = store.companies;

  if (companies.status === 'loading' || companies.status === 'loaded') {
    return Promise.resolve({ companies: Object.values(companies.data) });
  }

  ThunkApi.dispatch(loadingCompanies());

  return manApi
    .getAllCompany(store, true, true)
    .then(({ data: companiesData }) => {
      if (saveResults) {
        ThunkApi.dispatch(
          setCompaniesDataHandler_action({
            companies: companiesData,
            thisUserId: Number(store.thisUser.userId),
            noStub: false,
          })
        );
      }
      return Promise.resolve({
        companies: companiesData,
      });
    })
    .catch((err) => {
      ThunkApi.dispatch(notLoadedCompanies(err));
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getMyCompanies = createAsyncThunk(
  'companies/getMyCompanies',
  getMyCompaniesFn
);

export const createCompanyFn = async (
  { company }: { company: ICompany },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .createCompany(company, store)
    .then(({ data: company }) => {
      ThunkApi.dispatch(
        setSingleCompany({ company, thisUserId: Number(store.thisUser.userId) })
      );
      return Promise.resolve({
        company,
      });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const createCompany = createAsyncThunk(
  'companies/createCompany',
  createCompanyFn
);

export const updateCompanyFn = async (
  { company }: { company: ICompany },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi.updateCompany(company, store).then(
    ({ data: company }) => {
      ThunkApi.dispatch(
        setSingleCompany({ company, thisUserId: Number(store.thisUser.userId) })
      );
      return Promise.resolve({ company });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const updateCompany = createAsyncThunk(
  'companies/updateCompany',
  updateCompanyFn
);

export const syncDownStreamConfigFn = async (
  {
    companyId,
    downStreamConfigId,
  }: { companyId: number; downStreamConfigId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.syncDownStream(store, companyId, downStreamConfigId).then(
    ({ data: res }) => {
      return Promise.resolve({ res });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const syncDownStreamConfig = createAsyncThunk(
  'companies/syncDownStreamConfigFn',
  syncDownStreamConfigFn
);

export const updateCompanyDownStreamFn = async (
  {
    companyId,
    streamingConfig,
  }: { companyId: number; streamingConfig: IStreamingConfig },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .handleUpdateCompanyStreamingConfig(store, companyId, streamingConfig)
    .then(
      (res: any) => {
        console.log(res.data);
        return Promise.resolve({
          companyId,
          streamingConfig: res.data,
        });
      },
      (err) => {
        return ThunkApi.rejectWithValue(err) as any;
      }
    );
};
const updateCompanyDownStream = createAsyncThunk(
  'companies/syncDownStreamConfigFn',
  updateCompanyDownStreamFn
);

export const getCompanyHandlerFn = async (
  {
    companyId,
    templateStub = null,
    companyStub = null,
    projectStub = null,
    taskStub = null,
  }: {
    companyId: number;
    templateStub?: boolean | null;
    companyStub?: boolean | null;
    projectStub?: boolean | null;
    taskStub?: boolean | null;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const company = store.companies.data[Number(companyId)];

  if (
    company &&
    ((company.status === 'loaded' && projectStub) ||
      company.status === 'loading')
  ) {
    return Promise.resolve({ company });
  }

  ThunkApi.dispatch(
    loadingCompany({
      companyId: Number(companyId),
    })
  );
  return manApi
    .getCompany(
      store,
      companyId,
      templateStub,
      companyStub,
      projectStub,
      taskStub
    )
    .then(({ data: company }) => {
      ThunkApi.dispatch(
        getCompany({
          company: company,
          thisUserId: Number(store.thisUser.userId),
        })
      );
      return Promise.resolve({ company });
    })
    .catch((err) => {
      ThunkApi.dispatch(removeCompany({ companyId: Number(companyId) }));
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const getCompanyHandler = createAsyncThunk(
  'companies/getCompany',
  getCompanyHandlerFn
);

export const removeCompanyDocumentFn = async (
  { companyId, type }: { companyId: number; type: string },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.removeCompanyDocument(store, companyId, type).then(
    ({ data: company }) => {
      ThunkApi.dispatch(
        setSingleCompany({ company, thisUserId: Number(store.thisUser.userId) })
      );
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const removeCompanyDocument = createAsyncThunk(
  'companies/removeCompanyDocument',
  removeCompanyDocumentFn
);

export const addMemberToCompanyFn = async (
  {
    companyMember,
    noAction = false,
  }: { companyMember: ICompanyMember; noAction?: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.addCompanyMember(companyMember, store).then(
    ({ data: companyMemberData }) => {
      if (!noAction) {
        ThunkApi.dispatch(
          setCompanyMemberData({ companyMember: companyMemberData })
        );
      }
      return Promise.resolve({ companyMember: companyMemberData });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addMemberToCompany = createAsyncThunk(
  'companies/addMemberToCompany',
  addMemberToCompanyFn
);

export const migrateToProjectRolesFn = async (
  {
    companyId,
    toMigrateRoleNames,
  }: { companyId: number; toMigrateRoleNames: Array<string> },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .migrateToProjectRoles(store, companyId, toMigrateRoleNames)
    .then(({ data: companyData }) => {
      ThunkApi.dispatch(
        getCompanyReducer({
          company: companyData,
          thisUserId: Number(store.thisUser.userId),
        })
      );
      return Promise.resolve();
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const migrateToProjectRoles = createAsyncThunk(
  'companies/migrateToProjectRoles',
  migrateToProjectRolesFn
);

export const migrateRoleFn = async (
  {
    fromRole,
    toRole,
    deleteFrom,
  }: { fromRole: number; toRole: number; deleteFrom: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.migrateRole(store, fromRole, toRole, deleteFrom).then(
    () => {
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const migrateRole = createAsyncThunk('companies/migrateRole', migrateRoleFn);

export const getSingleCompanyFn = async (
  { companyId, projectStub }: { companyId: number; projectStub: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  try {
    const { data: company } = await manApi.getCompany(
      store,
      companyId,
      null,
      null,
      projectStub
    );
    return Promise.resolve({ company });
  } catch (err) {
    return ThunkApi.rejectWithValue(err) as any;
  }
};
const getSingleCompany = createAsyncThunk(
  'companies/getSingleCompany',
  getSingleCompanyFn
);

export const batchAddMembersToCompanyFn = async (
  {
    invitedMembers,
    forceRename,
    companyId,
  }: {
    invitedMembers: Array<IInvitedMember>;
    forceRename: boolean;
    companyId: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  try {
    const { data: users } = await manApi.companyAddMembers(
      invitedMembers,
      forceRename,
      companyId,
      store
    );
    const { data: company } = await manApi.getCompany(store, companyId);
    const companyUserIds = (company.members as Array<ICompanyMember>).map(
      (m) => m.userId
    );
    ThunkApi.dispatch(requireSeveralUser({ usersList: companyUserIds }));
    ThunkApi.dispatch(
      companiesBatchAddMembers({
        company,
        thisUserId: Number(store.thisUser.userId),
      })
    );
    return Promise.resolve({ users });
  } catch (err) {
    return ThunkApi.rejectWithValue(err) as any;
  }
};
const batchAddMembersToCompany = createAsyncThunk(
  'companies/batchAddMembersToCompany',
  batchAddMembersToCompanyFn
);

export const removeMemberFromCompanyFn = async (
  { companyMember }: { companyMember: ICompanyMember },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.removeCompanyMember(companyMember, store).then(
    () => {
      ThunkApi.dispatch(removeCompanyMember({ companyMember }));
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const removeMemberFromCompany = createAsyncThunk(
  'companies/removeMemberFromCompany',
  removeMemberFromCompanyFn
);

export const addTemplateFn = async (
  { companyTemplate }: { companyTemplate: ICompanyTemplate },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.addCompanyTemplate(companyTemplate, store).then(
    ({ data: companyTemplateData }) => {
      ThunkApi.dispatch(
        setCompanyTemplateDataHandler_action({ template: companyTemplateData })
      );
      ThunkApi.dispatch(
        tasks_setCompanyTemplateData({ template: companyTemplateData })
      );

      return Promise.resolve({ template: companyTemplateData });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addTemplate = createAsyncThunk('companies/addTemplate', addTemplateFn);

export const updateTemplateFn = async (
  { companyTemplate }: { companyTemplate: ICompanyTemplate },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  companyTemplate.task!['verifier'] = null;
  return manApi.updateCompanyTemplate(companyTemplate, store).then(
    ({ data: companyTemplateData }) => {
      ThunkApi.dispatch(
        setCompanyTemplateDataHandler_action({ template: companyTemplateData })
      );
      ThunkApi.dispatch(
        companySetTemplateData({ template: companyTemplateData })
      );
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const updateTemplate = createAsyncThunk(
  'companies/updateTemplate',
  updateTemplateFn
);

export const applyTemplateToPastTasksFn = async (
  { templateId }: { templateId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.pushCompanyTemplate(store, templateId).then(
    ({ data: countOfAppliedTasks }) => {
      return Promise.resolve({ countOfAppliedTasks });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const applyTemplateToPastTasks = createAsyncThunk(
  'companies/applyTemplateToPastTasks',
  applyTemplateToPastTasksFn
);

export const addCompanyRoleFn = async (
  { role, noAction = false }: { role: ICompanyRoleServer; noAction?: boolean },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi.addRole(store, role).then(
    ({ data: companyRoleData }) => {
      if (!noAction) {
        ThunkApi.dispatch(
          companiesSetRolesData({
            updateRole: companyRoleData,
            companyId: role.companyId as number,
          })
        );
      }
      return Promise.resolve({
        role: companyRoleData.updateRoles[0],
      });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addCompanyRole = createAsyncThunk(
  'companies/addCompanyRole',
  addCompanyRoleFn
);

export const updateCompanyRoleFn = async (
  { role }: { role: ICompanyRoleServer },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.updateRole(store, role).then(
    ({ data: companyRoleData }) => {
      ThunkApi.dispatch(
        companiesSetRolesData({
          updateRole: companyRoleData,
          companyId: role.companyId as number,
        })
      );
      return Promise.resolve({
        role: companyRoleData.updateRoles[0],
      });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const updateCompanyRole = createAsyncThunk(
  'companies/updateCompanyRole',
  updateCompanyRoleFn
);

export const addPrefixRolesFn = async (
  { role }: { role: ICompanyRoleServer },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.addAllPrefixRoles(store, role).then(
    ({ data: userRolesData }) => {
      ThunkApi.dispatch(
        companiesAddUserRoles({
          userRoles: userRolesData,
          companyId: role.companyId as number,
        })
      );
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addPrefixRoles = createAsyncThunk(
  'companies/addPrefixRoles',
  addPrefixRolesFn
);

export const deleteCompanyRoleFn = async (
  { roleId, companyId }: { roleId: number; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi.deleteRole(store, roleId).then(
    ({ data: companyRoleData }) => {
      ThunkApi.dispatch(
        companiesSetRolesData({ updateRole: companyRoleData, companyId })
      );
      if (companyRoleData.deleted.length) {
        return Promise.resolve();
      }
      return Promise.reject();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const deleteCompanyRole = createAsyncThunk(
  'companies/deleteCompanyRole',
  deleteCompanyRoleFn
);

export const addUserRolesFn = async (
  {
    userRoles,
    companyId,
    noAction = false,
  }: {
    userRoles: Array<IUserRoleView>;
    companyId: number;
    noAction?: boolean;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.addUserRoles(store, userRoles).then(
    ({ data: userRolesData }) => {
      if (!noAction) {
        ThunkApi.dispatch(
          companiesAddUserRoles({ userRoles: userRolesData, companyId })
        );
      }
      return Promise.resolve({
        userRoles: userRolesData,
      });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addUserRoles = createAsyncThunk('companies/addUserRoles', addUserRolesFn);

export const deleteUserRolesFn = async (
  {
    userRoles,
    companyId,
  }: { userRoles: Array<IUserRoleView>; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi.removeUserRoles(store, userRoles).then(
    () => {
      ThunkApi.dispatch(companiesRemoveUserRoles({ userRoles, companyId }));
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const deleteUserRoles = createAsyncThunk(
  'companies/deleteUserRoles',
  deleteUserRolesFn
);

export const addMemberSignatureFn = async (
  { companyId, userId, file }: { companyId: number; userId: number; file: any },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.addMemberSignature(store, companyId, userId, file).then(
    ({ data: companyMember }) => {
      ThunkApi.dispatch(setCompanyMemberData({ companyMember }));
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addMemberSignature = createAsyncThunk(
  'companies/addMemberSignature',
  addMemberSignatureFn
);

export const addCompanyDocumentFn = async (
  { companyId, type, file }: { companyId: number; type: string; file: any },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const mx = UserMixpanel.getInstance();
  return manApi.addCompanyDocument(store, companyId, type, file).then(
    ({ data: company }) => {
      ThunkApi.dispatch(
        setSingleCompany({ company, thisUserId: Number(store.thisUser.userId) })
      );
      mx.sendUploadDocumentTrack(`${type} - ${file}`);
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addCompanyDocument = createAsyncThunk(
  'companies/addCompanyDocument',
  addCompanyDocumentFn
);

export const updateCompanyMemberFn = async (
  { companyMember }: { companyMember: ICompanyMember },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi
    .updateCompanyMember(companyMember, store)
    .then(({ data: companyMember }) => {
      ThunkApi.dispatch(setCompanyMemberData({ companyMember }));
      return Promise.resolve({ companyMember });
    })
    .catch((err) => {
      return ThunkApi.rejectWithValue(err) as any;
    });
};
const updateCompanyMember = createAsyncThunk(
  'companies/updateCompanyMember',
  updateCompanyMemberFn
);

export const removeMemberSignatureFn = async (
  { companyId, userId }: { companyId: number; userId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.removeMemberSignature(store, companyId, userId).then(
    ({ data: companyMember }) => {
      ThunkApi.dispatch(setCompanyMemberData({ companyMember }));
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const removeMemberSignature = createAsyncThunk(
  'companies/removeMemberSignature',
  removeMemberSignatureFn
);

export const addCompanyPermissionSchemeFn = async (
  { role }: { role: ICompanyPermissionScheme },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.addPermissionScheme(store, role).then(
    ({ data: companyPermissionSchemeData }) => {
      ThunkApi.dispatch(
        companiesSetPermissionSchemeData({
          permissionScheme: companyPermissionSchemeData,
        })
      );
      return Promise.resolve({
        permissionScheme: companyPermissionSchemeData,
      });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addCompanyPermissionScheme = createAsyncThunk(
  'companies/addCompanyPermissionScheme',
  addCompanyPermissionSchemeFn
);

export const updateCompanyPermissionSchemeFn = async (
  { role }: { role: ICompanyPermissionScheme },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.updatePermissionScheme(store, role).then(
    ({ data: companyPermissionSchemeData }) => {
      ThunkApi.dispatch(
        companiesSetPermissionSchemeData({
          permissionScheme: companyPermissionSchemeData,
        })
      );
      return Promise.resolve({
        permissionScheme: companyPermissionSchemeData,
      });
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const updateCompanyPermissionScheme = createAsyncThunk(
  'companies/updateCompanyPermissionScheme',
  updateCompanyPermissionSchemeFn
);

export const deleteCompanyPermissionSchemeFn = async (
  { roleId, companyId }: { roleId: number; companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi.deletePermissionScheme(store, roleId).then(
    ({ data: canDelete }) => {
      if (canDelete) {
        ThunkApi.dispatch(
          companiesDeletePermissionSchemeData({
            companyId,
            permissionSchemeId: roleId,
          })
        );
      }
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const deleteCompanyPermissionScheme = createAsyncThunk(
  'companies/deleteCompanyPermissionScheme',
  deleteCompanyPermissionSchemeFn
);
export const exportMembersFn = (
  { companyId }: { companyId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi.exportCompanyMembers(store, companyId).then(
    (res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const fileNameHeader = 'x-suggested-filename';
      const suggestedFileName = res.headers[fileNameHeader];
      const effectiveFileName =
        suggestedFileName === undefined
          ? 'کاربران.xlsx'
          : decodeURIComponent(suggestedFileName);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', effectiveFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const exportMembers = createAsyncThunk(
  'companies/exportMembers',
  exportMembersFn
);

export const getCompanyMemberFn = async (
  { userId, companyId }: { userId: number; companyId: number },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return manApi
    .getCompanyMember(userId, companyId, store)
    .then(({ data: companyMemberData }) => {
      ThunkAPI.dispatch(
        setCompanyMemberData({ companyMember: companyMemberData })
      );
      return Promise.resolve(companyMemberData);
    })
    .catch((err) => Promise.reject(err));
};

const getCompanyMember = createAsyncThunk(
  'companies/getCompanyMember',
  getCompanyMemberFn
);

export const getCompanyMemberLogsFn = async (
  {
    userId,
    companyId,
    beforeId,
    count,
  }: {
    userId: number;
    companyId: number;
    beforeId: number | null;
    count: number | null;
  },
  ThunkAPI: TThunkApi
) => {
  const store = ThunkAPI.getState() as RootState;
  return manApi
    .getCompanyMemberLogs(userId, companyId, beforeId, count, store)
    .then(
      ({ data: companyMemberData }) => {
        return Promise.resolve(companyMemberData);
      },
      (err) => Promise.reject(err)
    );
};

const getCompanyMemberLogs = createAsyncThunk(
  'companies/getCompanyMemberLogs',
  getCompanyMemberLogsFn
);

const companiesSlice = createSlice({
  name: 'companies',
  initialState: initialState,
  reducers: {
    loadingCompanies: loadingCompaniesHandler,
    notLoadedCompanies: notLoadedCompaniesHandler,
    setSingleCompany: setSingleCompanyHandler,
    setSingleCompanyPDPData: setSingleCompanyPDPDataHandler,
    loadingCompany: loadingCompanyHandler,
    getCompanyReducer: getCompanyHandler_action,
    removeCompany: removeCompanyHandler,
    setCompanyMemberData: setCompanyMemberDataHandler,
    removeCompanyMember: removeCompanyMemberHandler,
    companiesBatchAddMembers: companiesBatchAddMembersHandler,
    companiesSetRoleData: companiesSetRoleDataHandler,
    companiesSetRolesData: companiesSetRolesDataHandler,
    companiesDeleteRoleData: companiesDeleteRoleDataHandler,
    companiesSetPermissionSchemeData: companiesSetPermissionSchemeDataHandler,
    companiesDeletePermissionSchemeData:
      companiesDeletePermissionSchemeDataHandler,
    companiesAddUserRoles: companiesAddUserRolesHandler,
    companiesRemoveUserRoles: companiesRemoveUserRolesHandler,
    addCompanyProviders: addCompanyProvidersHandler,
    createProject: createProjectHandler,
    setCompaniesDataHandler_action: setCompaniesDataHandler_actionHandler,
    setCompanyTemplateDataHandler_action:
      setCompanyTemplateDataHandler_actionHandler,
    setCompanyDownStream: setCompanyDownStream,
    //   Projects
    createProjectAction: createProjectHandler,
    setProjectMemberData: setProjectMemberDataHandler,
    removeProjectMemberAction: removeProjectMemberHandler,
    loadingSingleProject: loadingSingleProjectHandler,
    setSingleProjectAction: setSingleProjectHandler,
    setProjectTemplateData: setProjectTemplateDataHandler,
    removeTemplate: removeTemplateHandler,
    setCompaniesData: setCompaniesDataHandler,
    updateCompanyProject: updateCompanyProjectHandler,
  },
  extraReducers: (builder) => {
    builder.addCase(updateCompanyDownStream.fulfilled, (state, action) => {
      setCompanyDownStream(state, action);
    });
  },
});

export const {
  loadingCompanies,
  notLoadedCompanies,
  setSingleCompany,
  setSingleCompanyPDPData,
  loadingCompany,
  getCompanyReducer,
  removeCompany,
  setCompanyMemberData,
  removeCompanyMember,
  companiesBatchAddMembers,
  companiesSetRoleData,
  companiesSetRolesData,
  companiesDeleteRoleData,
  companiesSetPermissionSchemeData,
  companiesDeletePermissionSchemeData,
  companiesAddUserRoles,
  companiesRemoveUserRoles,
  addCompanyProviders,
  createProject,
  setCompaniesDataHandler_action,
  setCompanyTemplateDataHandler_action,
  //   Projects
  createProjectAction,
  setProjectTemplateData,
  removeProjectMemberAction,
  loadingSingleProject,
  setSingleProjectAction,
  removeTemplate,
  updateCompanyProject,
  setProjectMemberData,
} = companiesSlice.actions;

export {
  getMyCompanies,
  createCompany,
  updateCompany,
  getCompany,
  removeCompanyDocument,
  addMemberToCompany,
  migrateToProjectRoles,
  batchAddMembersToCompany,
  removeMemberFromCompany,
  addTemplate,
  updateTemplate,
  addCompanyRole,
  updateCompanyRole,
  addPrefixRoles,
  deleteCompanyRole,
  addUserRoles,
  deleteUserRoles,
  addMemberSignature,
  addCompanyDocument,
  updateCompanyMember,
  removeMemberSignature,
  addCompanyPermissionScheme,
  updateCompanyPermissionScheme,
  deleteCompanyPermissionScheme,
  exportMembers,
  applyTemplateToPastTasks,
  getSingleCompany,
  migrateRole,
  setCompanyTemplateData,
  setCompaniesData,
  setTemplateData,
  getCompanyMember,
  getCompanyMemberLogs,
  getCompanyHandler,
  syncDownStreamConfig,
  updateCompanyDownStream,
  //   Projects
  createCompanyProject,
  createProjects,
  getProject,
  addProjectMember,
  removeProjectMember,
  updateProjectMember,
  updateProject,
  addProjectTemplate,
  removeProjectTemplate,
};

export default companiesSlice.reducer;
